import React, { useEffect, useState } from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import AppTableContainer from '@projectK/components/AppTableContainer';
import TableHeading from './TableHeading';
import TableItem from './TableItem';
import { Box, Button, Typography, alpha } from '@mui/material';
import jwtAxios from '@projectK/services/auth/JWT';
import AppsPagination from '@projectK/components/AppsPagination';
import IntlMessages from '@projectK/helpers/IntlMessages';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Fonts } from '@projectK/constants';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import AppCard from '@projectK/components/AppCard';
import AddParentAccountMember from './AddParentAccountMember';
import AddNewParentMember from './AddNewParentMember';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import {isActualSuperAdmin,isActualAdmin} from '@projectK/context/RolesProvider';

const ParentMembers = () => {//NOSONAR
  const companyTheme = CompanyTheme();
  const { verbiageSettings } = useApplicationSettings();
  const infoViewActionsContext = useInfoViewActionsContext();
  const isMobile = useMediaQuery('(max-width:755px)');
  const { user } = useAuthUser();
  const [settings, setSettings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [isExistingMemberModalOpen, setIsExistingMemberModalOpen] =
    useState(false);
  const [isAddParentModalOpen, setIsAddParentModalOpen] = useState(false);
  useEffect(() => {
    fetchMembersData();
  }, [ page]);

  const fetchMembersData = async () => {
    try {
      const response = await jwtAxios.get(`/user-members/parents/${user?.id}`, {
        params: {
          page,
          perPage: 5,
        },
      });
      const { data } = response;
      setSettings(data?.list);
      setTotal(response?.data?.total);
      data?.list?.length === 0 && page > 0 && setPage(page - 1);
    } catch (error) {
      setSettings([]);
    }
  };

  //Delete
  const onSeperate = (itemId) => {
    jwtAxios
      .post(`/user-members/parent-seperate/${user.id}/${itemId}`)
      .then((response) => {
        fetchMembersData();
        infoViewActionsContext.showMessage('Parent seperated successfully!');
      })
      .catch((error) => {
        infoViewActionsContext.fetchError('Error deleting user');
        console.error('Error deleting user:', error);
      });
  };

  const onPageChange = (event, value) => {
    setPage(value);
  };


  const openExistingModal = () => {
    setIsExistingMemberModalOpen(true);
  };
  const closeExistingModal = () => {
    setIsExistingMemberModalOpen(false);
  };

  const openAddParentModal = () => {
    setIsAddParentModalOpen(true);
  };
  const closeAddParentModal = () => {
    setIsAddParentModalOpen(false);
  };

  return (
    <AppCard>
      <Typography
        component='h3'
        sx={{
          marginBottom: 2,
          fontSize: 16,
          fontWeight: Fonts.BOLD,
          color: companyTheme ? companyTheme.primary.main : 'text.primary',
        }}
      >
        {verbiageSettings.account_member_alias || (
          <IntlMessages id='common.accountMembers' />
        )}
      </Typography>
      <Typography component='h3' sx={{ marginBottom: 5 }}>
        These settings let you display family members (ex: parent).
      </Typography>

      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        width={1}
        justifyContent={isMobile ? 'space-between' : 'end'}
        marginBottom={5}
      >

        <Box display='flex' flexDirection='row' alignItems='center'>
          {(isActualSuperAdmin(user) || isActualAdmin(user)) ? ( //useroletag
            <Button
              variant='outlined'
              size='small'
              onClick={openExistingModal}
              sx={{
                position: 'relative',
                ml: 2.5,
                ...(isMobile ? { minWidth: 0 } : { minWidth: 100 }),
                borderColor: companyTheme
                  ? companyTheme.primary.main
                  : '#2196f3',
                '&:hover': {
                  borderColor: companyTheme
                    ? alpha(companyTheme.primary.main, 0.8)
                    : alpha('#2196f3', 0.8),
                },
                color: companyTheme ? companyTheme.primary.main : '#2196f3',
              }}
            >
              <AddOutlinedIcon sx={{ mr: 1 }} />
              Add Existing Parents
            </Button>
          ) : (
            ''
          )}
          {(isActualSuperAdmin(user)|| isActualAdmin(user)) ? ( 
            <Button
              variant='outlined'
              size='small'
              onClick={openAddParentModal}
              sx={{
                position: 'relative',
                ml: 2.5,
                ...(isMobile ? { minWidth: 0 } : { minWidth: 100 }),
                borderColor: companyTheme
                  ? companyTheme.primary.main
                  : '#2196f3',
                '&:hover': {
                  borderColor: companyTheme
                    ? alpha(companyTheme.primary.main, 0.8)
                    : alpha('#2196f3', 0.8),
                },
                color: companyTheme ? companyTheme.primary.main : '#2196f3',
              }}
            >
              <AddOutlinedIcon sx={{ mr: 1 }} />
              Add New Parent
            </Button>
          ) : (
            ''
          )}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <AppsPagination
              rowsPerPage={5}
              count={total}
              page={page}
              onPageChange={onPageChange}
            />
       </Box>
        </Box>
        <Box sx={{ display: { xs: "block", sm: "none" } }}>
          <AppsPagination
            className='mtm12'
            rowsPerPage={5}
            count={total}
            page={page}
            onPageChange={onPageChange}
          />
        </Box>
      </Box>
      {isExistingMemberModalOpen && (<AddParentAccountMember
        isOpen={isExistingMemberModalOpen}
        onClose={closeExistingModal}
        fetchMembersData={fetchMembersData}
      />)}
      <AddNewParentMember
        isOpen={isAddParentModalOpen}
        onClose={closeAddParentModal}
        fetchMembersData={fetchMembersData}
      />
      <AppTableContainer>
        <Table className='table'>
          <TableHead>
            <TableHeading user={user} />
          </TableHead>
          <TableBody>
          {settings?.map((item, index) => (
          <TableItem
            key={index +1}
            data={item}
            fetchMembersData={fetchMembersData}
            onSeperate={onSeperate}
            companyTheme={companyTheme}
           />
          ))}
          </TableBody>
        </Table>
      </AppTableContainer>
    </AppCard>
  );
};

export default ParentMembers;
